import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT, UPDATE } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axiosServices from 'utils/axios';
import toast from 'react-hot-toast';
// types
import { KeyedObject } from 'types';
import { InitialLoginContextProps, JWTContextType, PayProps } from 'types/auth';
import useConfig from 'hooks/useConfig';

const chance = new Chance();

// constant
const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    cdkeys: [],
    promotion: undefined
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded: KeyedObject = jwtDecode(serviceToken);

    /**
     * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken?: string | null) => {
    if (serviceToken) {
        localStorage.setItem('serviceToken', serviceToken);
        axiosServices.defaults.headers.common.Authorization = `jwt ${serviceToken}`;
    } else {
        localStorage.removeItem('serviceToken');
        delete axiosServices.defaults.headers.common.Authorization;
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    const { locale } = useConfig();
    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = window.localStorage.getItem('serviceToken');
                if (serviceToken && verifyToken(serviceToken)) {
                    setSession(serviceToken);
                    const { data } = await axiosServices.get('/web/info');
                    const cdkeysInfo = await axiosServices.get('/web/get_cdkey_list');
                    const promotion = await getPromotion(data.user.id);
                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user: data.user,
                            cdkeys: cdkeysInfo.data.cdkeys.data,
                            promotion
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                console.error(err);
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const login = async (email: string, password: string) => {
        const response = await axiosServices.post('/auth', { email, password });
        const { access_token } = response.data;
        setSession(access_token);
        const { data } = await axiosServices.get('/web/info');
        const cdkeysInfo = await axiosServices.get('/web/get_cdkey_list');

        const promotion = await getPromotion(data.user.id);

        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user: data.user,
                cdkeys: cdkeysInfo.data.cdkeys.data,
                promotion
            }
        });
    };

    const register = async (email: string, password: string, firstName: string, lastName: string) => {
        // todo: this flow need to be recode as it not verified
        const id = chance.bb_pin();
        const response = await axiosServices.post('/api/account/register', {
            id,
            email,
            password,
            firstName,
            lastName
        });
        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers!),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`
                }
            ];
        }

        window.localStorage.setItem('users', JSON.stringify(users));
    };

    const logout = () => {
        setSession(null);
        window.localStorage.removeItem('serviceToken');
        dispatch({
            type: LOGOUT
        });
    };

    const resetPassword = async (params: Record<string, string>) => {
        const response = await axiosServices.post('/web/reset_password', params);
        return response.data;
    };

    const updatePassword = async (params: Record<string, string>) => {
        const response = await axiosServices.post('/web/update_password', params);
        return response.data;
    };

    const sendRsetEmail = async (email: string) => {
        const { data } = await axiosServices.get(`/web/send_reset_email?email=${email}&lang=${locale}`);

        if (data.status === 2) {
            toast.error(data.status_text);
        } else {
            toast.success(data.status_text);
        }

        return data.status !== 2;
    };

    const updateProfile = async () => {
        const { data } = await axiosServices.get('/web/info');
        const cdkeysInfo = await axiosServices.get('/web/get_cdkey_list');
        dispatch({
            type: UPDATE,
            payload: {
                isLoggedIn: true,
                user: data.user,
                cdkeys: cdkeysInfo.data.cdkeys.data
            }
        });
    };

    const pay = async (params: PayProps) => {
        if (params.type === 'stripe') {
            let visaParasm = { ...params, type: undefined };
            const response = await axiosServices.post(params.way, visaParasm);
            return response.data;
        }

        const response = await axiosServices.post(params.way, params);
        return response.data;
    };

    const getDevices = async () => {
        const response = await axiosServices.get('/web/get_devices');
        return response.data;
    };

    const deleteDevices = async (params: { device_id: number }) => {
        const response = await axiosServices.post('/web/delete_device', params);
        return response.data;
    };

    const getBonusLog = async () => {
        const response = await axiosServices.get('/web/bonus_log');
        return response.data;
    };

    const getPromotion = async (uid: string | number) => {
        const { data } = await axiosServices.get(`/pay/get_promotion?uid=${uid}`);

        let promotionInfo = {
            discount: data.discount,
            promotion: data.promotion,
            seconds: data.seconds,
            showCountdown: data.show_countdown,
            timestamp: data.timestamp
        };
        return promotionInfo;
        // const { data } = await axios.get(`/pay/get_promotion_debug?debug=0.95`);
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider
            value={{
                ...state,
                login,
                logout,
                register,
                resetPassword,
                updatePassword,
                sendRsetEmail,
                updateProfile,
                pay,
                getDevices,
                deleteDevices,
                getBonusLog
            }}
        >
            {children}
        </JWTContext.Provider>
    );
};

export default JWTContext;
